import { useMemo } from 'react';
import type { TFunction } from 'i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';

/**
 * Returns a callback that always runs the latest `callback` and keeps its identity until `t` changes. `t`
 * changes on a language switch, so a memoized consumer of the callback, such as a select's
 * `formatOptionLabel`, renders again with the new translation.
 */
export const useTranslationCallbackRef = <Args extends unknown[], Result>(
  callback: (...args: Args) => Result,
  t: TFunction,
): ((...args: Args) => Result) => {
  const latestCallback = useCallbackRef(callback);

  return useMemo(
    () =>
      (...args: Args) =>
        latestCallback(...args),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` renews the identity; the callback reads it through `latestCallback`
    [latestCallback, t],
  );
};
